import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, BosDurum, Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, recaptchaAyarla, recaptchaDurumu } from "./api";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * reCAPTCHA v3 YÖNETİMİ — F-AUTH
 *
 * ================================================================
 * "ENTEGRASYONLAR" EKRANINDA, AYRI MENÜDE DEĞİL
 * ================================================================
 * İzni `sistem.entegrasyon.duzenle` — Entegrasyonlar menüsünün
 * iznyle AYNI. Ayrı bir menü girdisi açsaydım iki girdi aynı iki
 * kişiye görünür ve biri ötekini aramak zorunda kalırdı.
 *
 * ================================================================
 * GİZLİ ANAHTAR BİR DAHA OKUNAMAZ
 * ================================================================
 * Sunucu onu şifreleyerek saklıyor ve hiçbir uçtan döndürmüyor.
 * Ekran o yüzden alanı BOŞ gösteriyor ve "boş bırakırsanız mevcut
 * anahtar korunur" diyor. Boş alanı "sil" saysaydık, site anahtarını
 * güncellemek isteyen kişi farkında olmadan korumayı devre dışı
 * bırakırdı.
 *
 * ================================================================
 * DOĞRULAYAMAYAN AMA AÇIK GÖRÜNEN KORUMA
 * ================================================================
 * Sunucu, gizli anahtar yokken entegrasyonu AKTİF ETMİYOR. Ekran da
 * bunu önceden söylüyor: "aktif" kutusu, anahtar hiç girilmemişse
 * kapalı ve sebebi yazılı. Korumanın en kötü hâli, koruduğunu sanmak.
 */
/*
  DOSYA ADI `RecaptchaAyar`: `recaptcha.ts` (giris ekraninin istemci
  betigi) zaten var ve Windows dosya sistemi buyuk/kucuk harf ayrimi
  yapmiyor — `Recaptcha.tsx` ile cakisiyordu. TypeScript yakaladi.
*/
export function RecaptchaAyar() {
  const qc = useQueryClient();
  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "reCAPTCHA anahtarları hassas; kimliğinizi yeniden doğrulayın.",
  );
  const [siteAnahtari, setSiteAnahtari] = useState("");
  const [gizliAnahtar, setGizliAnahtar] = useState("");
  const [aktif, setAktif] = useState(false);
  const [esik, setEsik] = useState("0.5");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const durum = useQuery({
    queryKey: ["recaptcha-durum"],
    queryFn: recaptchaDurumu,
    retry: false,
  });

  /*
    Sunucudan gelen değerler forma BİR KEZ yükleniyor. Her yenilemede
    yüklenseydi, kullanıcı yazarken alanı altından çekerdi.
  */
  useEffect(() => {
    if (!durum.data) return;
    setSiteAnahtari(durum.data.siteAnahtari ?? "");
    setAktif(durum.data.aktif);
    setEsik(String(durum.data.esik));
  }, [durum.data]);

  const kaydet = useMutation({
    mutationFn: () =>
      recaptchaAyarla({
        siteAnahtari: siteAnahtari.trim(),
        // Boş = DEĞİŞTİRME. `null` gönderiliyor, boş dizgi değil:
        // sunucu şemasında boş dizgi "en az 10 karakter"e takılırdı.
        gizliAnahtar: gizliAnahtar.trim() || null,
        aktif,
        esik: Number(esik),
      }),
    // Sudo penceresi kapaliysa sunucu 403 donuyor; kapi acilip islem
    // KALDIGI YERDEN devam ediyor (`SudoKapisi.tsx` kalibi).
    onError: hataYakala(() => kaydet.mutate()),
    onSuccess: async (s) => {
      /*
        İSTENENLE OLANIN FARKI SÖYLENİYOR. Sunucu, gizli anahtar
        yokken "aktif" isteğini kabul edip KAPALI kaydediyor —
        doğrulayamayan bir korumanın açık görünmemesi doğru. Yanlış
        olan sessiz olmasıydı: kullanıcı korumayı açtığını sanırdı.
      */
      setBilgi(
        s.aktif
          ? "Kaydedildi ve koruma AÇILDI — doğrulama başarılı."
          : /*
               SUNUCUNUN SEBEBI ONCE. Koruma acilmadiysa neden
               acilmadigini sunucu biliyor (jeton yok · jeton
               dogrulanamadi · gizli anahtar yok); ekranin kendi
               tahmini onun yerine gecmemeli.
            */
            (s.neden ??
              (aktif
                ? "Kaydedildi ama koruma AÇILMADI: gizli anahtar olmadan doğrulama yapılamaz."
                : "Kaydedildi. Koruma kapalı.")),
      );
      // Sunucunun GERCEK durumu ekrana yansiyor; kutu istenen degil
      // olan durumu gostermeli.
      setAktif(s.aktif);
      setGizliAnahtar("");
      await qc.invalidateQueries({ queryKey: ["recaptcha-durum"] });
    },
  });

  if (durum.isPending) return <Iskelet satir={3} />;

  if (durum.isError) {
    const kapi = sorguKapisi(durum.error, () => void durum.refetch());
    if (kapi) return kapi;
    return (
      <Kart baslik="Giriş koruması (reCAPTCHA)">
        <BosDurum
          ton="hata"
          baslik="Durum alınamadı"
          aciklama={durum.error instanceof ApiHatasi ? durum.error.message : undefined}
        />
      </Kart>
    );
  }

  /*
    Anahtar HİÇ girilmemişse `siteAnahtari` null gelir. Gizli anahtarın
    varlığını sunucu SÖYLEMİYOR (söyleseydi de gösteremezdik); site
    anahtarının varlığı, yapılandırmanın hiç yapılmadığını anlamak için
    yeterli tek işaret.
  */
  const hicYapilandirilmamis = durum.data.siteAnahtari === null;

  return (
    <Kart
      baslik="Giriş koruması (reCAPTCHA v3)"
      aciklama="Giriş ekranına bot koruması ekler. Anahtarlar Google reCAPTCHA konsolundan alınır."
      eylem={durum.data.aktif ? <Rozet ton="onay">Açık</Rozet> : <Rozet>Kapalı</Rozet>}
    >
      {kapi}
      <div className="flex flex-col gap-3">
        {hicYapilandirilmamis && (
          <p className="text-notr-700 text-sm">
            Henüz yapılandırılmamış. Koruma yüklenmiyor ve giriş ekranı bunu bilerek çalışıyor —
            yarım yapılandırma, hiç yapılandırmamaktan kötüdür.
          </p>
        )}

        <Alan
          etiket="Site anahtarı"
          value={siteAnahtari}
          onChange={(e) => setSiteAnahtari(e.target.value)}
          yardim="Gizli değildir; tarayıcı kaynağında zaten görünür."
        />

        <Alan
          etiket="Gizli anahtar"
          type="password"
          autoComplete="off"
          value={gizliAnahtar}
          onChange={(e) => setGizliAnahtar(e.target.value)}
          yardim="Şifrelenerek saklanır ve BİR DAHA GÖSTERİLMEZ. Boş bırakırsanız mevcut anahtar korunur — silinmez."
        />

        <Alan
          etiket="Eşik (0–1)"
          inputMode="decimal"
          value={esik}
          onChange={(e) => setEsik(e.target.value)}
          yardim="Google 0–1 arası bir puan döndürür; bu değerin ALTI bot sayılır. 0.5 dengeli bir başlangıç."
        />

        {/*
            ================================================================
            YANLIŞ SİTE ANAHTARI = HERKESİ DIŞARIDA BIRAKMA
            ================================================================
            08.08.2026'da canlı denemede yaşandı: sahte anahtarlarla
            aktif edildi ve HİÇ KİMSE giriş yapamaz oldu — düzeltmesi
            gereken kişi dahil. Sunucunun üç ayrı "açık kal" koruması
            var (Google'a ulaşılamıyor · anahtar çözülemiyor · sağlayıcı
            hatası) ama hiçbiri bu durumu kapsamıyor: site anahtarı
            yanlışsa tarayıcı JETON ÜRETEMİYOR ve istek `JETON_YOK`
            ile reddediliyor. Sunucu açısından bu, jetonsuz gelen bir
            bot isteğinden ayırt edilemez.

            Ekran bunu AÇIK AÇIK söylüyor. Uyarı yerine sessiz bir
            onay kutusu bırakmak, tek yanlış karakterin sistemi
            kilitlemesi demekti.
          */}
        {!durum.data.aktif && (
          <p className="rounded-kart shadow-kart border-uyari-500 bg-uyari-50 text-notr-800 border px-3 py-2 text-sm">
            <strong>Açmadan önce anahtarları iki kez kontrol edin.</strong> Site anahtarı yanlışsa
            tarayıcı doğrulama jetonu üretemez ve <strong>hiç kimse giriş yapamaz</strong> — bu
            ekrana ulaşıp düzeltmesi gereken kişi dahil. O durumda tek çıkış yolu veritabanından
            elle kapatmaktır.
          </p>
        )}

        <label className="min-h-dokunma flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            className="accent-marka-600 size-5 disabled:cursor-not-allowed"
            checked={aktif}
            /*
                Anahtar hiç girilmemişken AKTİF EDİLEMİYOR. Sunucu da
                reddediyor; kutuyu açık bırakmak, doğrulayamayan bir
                korumayı açık göstermek olurdu.
              */
            disabled={hicYapilandirilmamis && !gizliAnahtar.trim()}
            onChange={(e) => setAktif(e.target.checked)}
          />
          <span className="text-notr-800 text-sm">
            Girişte reCAPTCHA doğrulaması yapılsın
            {hicYapilandirilmamis && !gizliAnahtar.trim() && (
              <span className="text-notr-600 ml-2 text-xs">
                — gizli anahtar girilmeden açılamaz
              </span>
            )}
          </span>
        </label>

        {kaydet.isError && !(kaydet.error instanceof ApiHatasi && kaydet.error.sudoGerekliMi) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}

        <div>
          <Buton
            yukleniyor={kaydet.isPending}
            disabled={siteAnahtari.trim().length < 10}
            onClick={() => {
              setBilgi(null);
              kaydet.mutate();
            }}
          >
            Kaydet
          </Buton>
          <p className="text-notr-600 mt-1.5 text-xs">
            Bu işlem şifrenizi yeniden sorar — anahtar yazmak hassas bir işlem.
          </p>
        </div>
      </div>
    </Kart>
  );
}
